import { defineStore } from 'pinia';
import _ from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import { adminClient as apolloClient } from '@/store/apollo';
import CmsApi from '@/store/api/cms/cms';
import UserApi from '@/store/api/system/user';
import AccountApi from '@/store/api/system/account';
import SentEmailsGql from '@/store/graphql/system/queries/SentEmails.gql';

const defaultForm = {
    gender: 'M',
    first_name: '',
    last_name: '',
    email: '',
    emailVerified: true,
    sendEmailVerification: false,
    password: '',
    changePassword: false,
    phone: '',
    company: '',
    addresses: [
        {
            type: 'main',
            street: '',
            zipcode: '',
            city: '',
            country: '',
            default_billing: true,
            default_shipping: true
        }
    ],
    team: null
};

const defaultRules = {
    gender: 'required',
    first_name: 'required',
    last_name: 'required',
    email: 'required|email',
    password: 'required',
    phone: '',
    images: '',
    company: '',
    addresses: [
        {
            street: 'required',
            zipcode: 'required',
            city: 'required',
            country: 'required'
        }
    ]
};

const defaultUi = {
    form: {
        initialized: false,
        validateOnEnter: false
    }
};

export const useAccountStore = defineStore('account', {
    state: () => ({
        loading: true,
        accountsPaginated: {
            scope: 'base',
            type: 'account',
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 25,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        invoicesPaginated: {
            scope: 'base',
            type: 'billing_invoice',
            variables: {
                paginationSizes: [10, 25, 50],
                order: 'created_at',
                direction: 'desc'
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 10,
                currentPage: 1,
                lastPage: 1,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0
                }
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: [],
            loading: false
        },
        options: {
            template: {
                loading: false,
                items: []
            },
            cms: {
                loading: false,
                items: []
            }
        },
        verificationEmails: [],
        verificationEmailsLoading: false,
        form: _.cloneDeep(defaultForm),
        rules: _.cloneDeep(defaultRules),
        ui: _.cloneDeep(defaultUi)
    }),

    getters: {
        form_initialized: (state) => state.ui.form.initialized,
        form_validateOnEnter: (state) => state.ui.form.validateOnEnter,
        form_addresses: (state) => state.form.addresses
    },

    actions: {
        // Pagination mutations
        addPaginatedAccountsToState(data) {
            this.accountsPaginated.items = data.items;
            this.accountsPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.accountsPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.accountsPaginated.link = _.cloneDeep(data.config.link);
            this.accountsPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.accountsPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.accountsPaginated.allColumns, column => {
                const allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.accountsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.accountsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.accountsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.accountsPaginated.filters, key);
            } else {
                Vue.set(this.accountsPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.accountsPaginated, 'showFilters', value);
        },

        defineSorting({ key, value }) {
            Vue.set(this.accountsPaginated.variables, 'order', key);
            Vue.set(this.accountsPaginated.variables, 'direction', value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        resetRules() {
            Vue.set(this, 'rules', _.cloneDeep(defaultRules));

            this.form.addresses.forEach((address, index) => {
                Vue.set(this.rules.addresses, index, {
                    street: 'required',
                    zipcode: 'required',
                    city: 'required',
                    country: 'required'
                });
            });

            if (this.form.id) {
                Vue.set(this.rules, 'password', '');
            }
        },

        resetUI() {
            Vue.set(this, 'ui', _.cloneDeep(defaultUi));
        },

        insertAccount(account) {
            const addresses = _.get(account, 'addresses', []);
            if (!addresses.length) {
                account.addresses = [];
                account.addresses.push({
                    type: 'main',
                    street: '',
                    zipcode: '',
                    city: '',
                    country: '',
                    default_billing: true,
                    default_shipping: true
                });
            } else if (addresses.length === 1) {
                account.addresses[0].default_billing = true;
                account.addresses[0].default_shipping = true;
            }

            account.emailVerified = !!_.get(account, 'email_verified_at');
            account.sendEmailVerification = false;

            Vue.set(this, 'form', account);
        },

        updateCmsOptionsLoading(value) {
            Vue.set(this.options.cms, 'loading', value);
        },

        insertCmsOptions(items) {
            Vue.set(this.options.cms, 'items', items);
        },

        updateTemplateOptionsLoading(value) {
            Vue.set(this.options.template, 'loading', value);
        },

        insertTemplateOptions(templates) {
            Vue.set(this.options.template, 'items', templates);
        },

        // Form UI mutations
        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        // Form field mutations
        setFormGender(value) {
            Vue.set(this.form, 'gender', value);
        },

        setFormFirstName(value) {
            Vue.set(this.form, 'first_name', value);
        },

        setFormLastName(value) {
            Vue.set(this.form, 'last_name', value);
        },

        setFormEmail(value) {
            Vue.set(this.form, 'email', value);
        },

        setFormPassword(value) {
            Vue.set(this.form, 'password', value);
        },

        setFormChangePassword(value, requireStrength = false) {
            Vue.set(this.form, 'changePassword', value);
            if (value) Vue.set(this.rules, 'password', requireStrength ? 'required|password_strength' : 'required');
            else Vue.set(this.rules, 'password', '');
        },

        setFormPhone(value) {
            Vue.set(this.form, 'phone', value);
        },

        setFormCompany(value) {
            Vue.set(this.form, 'company', value);
        },

        setFormEmailVerified(value) {
            Vue.set(this.form, 'emailVerified', value);
            if (value) {
                Vue.set(this.form, 'sendEmailVerification', false);
            }
        },

        setFormSendEmailVerification(value) {
            Vue.set(this.form, 'sendEmailVerification', value);
        },

        resendEmailVerification() {
            var account = _.cloneDeep(this.form);
            account.sendEmailVerification = true;
            return UserApi.update(account).then(account => {
                this.insertAccount(_.cloneDeep(account));
                return account;
            });
        },

        // Address mutations
        setFormStreet({ i, value }) {
            Vue.set(this.form.addresses[i], 'street', value);
        },

        setFormZipcode({ i, value }) {
            Vue.set(this.form.addresses[i], 'zipcode', value);
        },

        setFormCity({ i, value }) {
            Vue.set(this.form.addresses[i], 'city', value);
        },

        setFormCountry({ i, value }) {
            Vue.set(this.form.addresses[i], 'country', value);
        },

        setFormDefaultBilling({ i, value }) {
            Vue.set(this.form.addresses[i], 'default_billing', value);
        },

        setFormDefaultShipping({ i, value }) {
            Vue.set(this.form.addresses[i], 'default_shipping', value);
        },

        setFormLongitude({ i, value }) {
            Vue.set(this.form.addresses[i], 'longitude', value);
        },

        setFormLatitude({ i, value }) {
            Vue.set(this.form.addresses[i], 'latitude', value);
        },

        // Team mutations
        teamUserDisable(user) {
            const id = user.id;
            const users = _.get(this, 'form.team.users') || [];
            const i = _.findIndex(users, u => u.id === id);

            if (i >= 0) {
                Vue.set(this.form.team.users[i].teamUserPivot, 'disabled', true);
            }
        },

        teamUserEnable(user) {
            const id = user.id;
            const users = _.get(this, 'form.team.users') || [];
            const i = _.findIndex(users, u => u.id === id);

            if (i >= 0) {
                Vue.set(this.form.team.users[i].teamUserPivot, 'disabled', false);
            }
        },

        teamMarkUserAsDeleted(user) {
            const id = user.id;
            const users = _.get(this, 'form.team.users') || [];
            const i = _.findIndex(users, u => u.id === id);

            if (i >= 0) {
                Vue.set(this.form.team.users[i].teamUserPivot, 'deleted', true);
            }
        },

        teamUnmarkUserAsDeleted(user) {
            const id = user.id;
            const users = _.get(this, 'form.team.users') || [];
            const i = _.findIndex(users, u => u.id === id);

            if (i >= 0) {
                Vue.set(this.form.team.users[i].teamUserPivot, 'deleted', false);
            }
        },

        teamAddUser(user) {
            if (!this.form.team) {
                Vue.set(this.form, 'team', {
                    id: uniqid('new:'),
                    users: []
                });
            }

            user = _.cloneDeep(user);
            user.teamUserPivot = {
                id: uniqid('new:'),
                disabled: false
            };
            const i = _.get(this, 'form.team.users.length') || 0;
            Vue.set(this.form.team.users, i, user);
        },

        teamUpdateUser(user) {
            const id = user.id;
            const users = _.get(this, 'form.team.users') || [];
            const i = _.findIndex(users, u => u.id === id);

            if (i >= 0) {
                const administrators = _.get(user, 'administrators') || [];
                Vue.set(this.form.team.users[i], 'administrators', administrators);

                const notify = _.get(user, 'notify') || false;
                Vue.set(this.form.team.users[i], 'notify', notify);
            }
        },

        // Invoices pagination mutations
        addPaginatedInvoicesToState(data) {
            this.invoicesPaginated.items = data.items;
            this.invoicesPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.invoicesPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.invoicesPaginated.link = _.cloneDeep(data.config.link);
            this.invoicesPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.invoicesPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineInvoicesActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.invoicesPaginated.allColumns, column => {
                const allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.invoicesPaginated.activeColumns = activeColumns;
            }
        },

        defineInvoicesItemsPerPage(perPage) {
            this.invoicesPaginated.pageInfo.perPage = perPage;
        },

        defineInvoicesCurrentPage(currentPage) {
            this.invoicesPaginated.pageInfo.currentPage = currentPage;
        },

        defineInvoicesFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && (value.in && value.in.length === 0)) {
                Vue.delete(this.invoicesPaginated.filters, key);
            } else {
                Vue.set(this.invoicesPaginated.filters, key, value);
            }
        },

        defineInvoicesFilterVisibility(value) {
            Vue.set(this.invoicesPaginated, 'showFilters', value);
        },

        defineInvoicesSorting({ key, value }) {
            Vue.set(this.invoicesPaginated.variables, 'order', key);
            Vue.set(this.invoicesPaginated.variables, 'direction', value);
        },

        updateInvoicesLoading(value) {
            Vue.set(this.invoicesPaginated, 'loading', value);
        },

        // Async actions
        fetchInvoicesPaginated(userId = null) {
            this.updateInvoicesLoading(true);
            return AccountApi.fetchInvoicesPaginated(this.invoicesPaginated, userId)
                .then(data => {
                    this.addPaginatedInvoicesToState(data);

                    const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                    this.defineInvoicesActiveColumns(activeColumnKeys);
                })
                .finally(() => {
                    this.updateInvoicesLoading(false);
                });
        },

        fetchAccountsPaginated() {
            return UserApi.fetchUsersPaginated(this.accountsPaginated).then(data => {
                this.addPaginatedAccountsToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchAccount(id) {
            return UserApi.fetchUser(id).then(account => {
                this.insertAccount(_.cloneDeep(account));
            });
        },

        fetchCmsOptions(ownerID) {
            this.updateCmsOptionsLoading(true);
            return CmsApi.fetchList(ownerID)
                .then(options => {
                    this.updateCmsOptionsLoading(false);
                    this.insertCmsOptions(options);
                })
                .catch(err => {
                    this.updateCmsOptionsLoading(false);
                    throw err;
                });
        },

        create(account) {
            return UserApi.create(account).then(account => {
                this.insertAccount(_.cloneDeep(account));
                this.resetRules();
                return account;
            });
        },

        update(account) {
            return UserApi.update(account).then(account => {
                this.insertAccount(_.cloneDeep(account));
                this.resetRules();
                return account;
            });
        },

        delete(id) {
            return UserApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        fetchVerificationEmails() {
            const email = _.get(this.form, 'email');
            if (!email) return Promise.resolve([]);

            this.verificationEmailsLoading = true;
            return apolloClient
                .query({
                    query: SentEmailsGql,
                    variables: {
                        recipient_email: email,
                        type: 'verify-email',
                    },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => {
                    this.verificationEmails = data.SentEmails || [];
                    return this.verificationEmails;
                })
                .finally(() => {
                    this.verificationEmailsLoading = false;
                });
        },

        reset() {
            this.insertAccount(_.cloneDeep(defaultForm));
        }
    }
});
